<template>
  <v-card outlined class="dd-card pa-2" @click="$emit('click', variable)">
    <div class="d-flex align-center flex-nowrap">
      <img
        :src="'/images/' + variable.category + '-icon-128.png'"
        :title="variable.category"
        style="height: 1.75em;"
        @error="missingDomainFallback"
      />
      <v-tooltip top color="primary">
        <template v-slot:activator="{ on: tooltip }">
          <span class="ml-2 text-body-2 text-truncate" v-on="{ ...tooltip }">
            {{ name }}
          </span>
        </template>
        <span>{{ variable.description || variable.label }}</span>
      </v-tooltip>
      <v-spacer />
      <span class="dd-type ml-1">{{ continuous ? 'cont.' : 'categ.' }}</span>
    </div>

    <div
      class="d-flex justify-center dd-chart my-2"
      :style="{ height: chartRowHeight + 'px' }"
    >
      <template v-if="stats.n > 0">
        <!-- just two labels, so the bar heights read as a number of subjects
             without spending a card on tick marks -->
        <div
          class="dd-yaxis mr-1"
          :style="{ height: axisHeight + 'px', width: axisWidth }"
        >
          <span class="dd-tick" :style="{ bottom: tickBottom + 'px' }">
            {{ tick }}
          </span>
          <span class="dd-tick" style="bottom: 0;">0</span>
        </div>
        <div class="dd-plot">
          <histogram-sparkline
            :id="chartId"
            :key="chartId"
            :type="variable.type"
            :scale-id="variable.id"
            :data-category="variable.data_category"
            :values="values"
            :width="chartWidth"
            :height="chartHeight"
            responsive
            @axes="axes = $event"
          />

          <!-- Reserved on every card, so a labelled plot and an unlabelled one
               put their footers on the same line. Left empty when there are too
               many levels to read; the footer still gives the count and the
               drill-down has the full chart. -->
          <div class="dd-xaxis" :style="{ height: X_AXIS_HEIGHT + 'px' }">
            <span
              v-for="(l, i) in xLabels"
              :key="'dd-x-' + i"
              class="dd-xlabel"
              :title="l"
            >
              {{ l }}
            </span>
          </div>
        </div>
      </template>
      <span v-else class="dd-empty">no data at baseline</span>
    </div>

    <div class="dd-footer">{{ footer }}</div>
  </v-card>
</template>

<script>
import { mapState } from 'vuex';
import { state } from '@/store/modules/datasetSummary/types';
import {
  DATA_CATEGORY_CONTINUOUS,
  axisTick,
  baselineValuesBySubject,
  formatPercent,
  formatStat,
  summaryStats,
} from '@/utils/dashboard';
import { missingDomainFallback } from '@/utils/helpers';
import HistogramSparkline from '@/components/DatasetSummary/HistogramSparkline.vue';

// charts/HistogramSparkline and charts/ColumnChart both inset their drawing
// vertically by this much, so the axis labels are placed against the svg box
// rather than against the taller row that holds it
const CHART_VERTICAL_MARGIN = 4;

// must track the .dd-tick font-size, since a label is centred on its value by
// offsetting half its own height
const TICK_LABEL_HEIGHT = 9;

// height of the band under every plot, labelled or not
const X_AXIS_HEIGHT = 12;

// Past this, a card's width leaves so few pixels per label that they truncate
// to nothing legible. Six across a 240px card is about 40px each.
const MAX_LABELLED_LEVELS = 6;

export default {
  name: 'SummaryCard',
  components: {
    HistogramSparkline,
  },
  props: {
    // a dashboard variable, carrying `uid` (see variableKey)
    variable: {
      type: Object,
      required: true,
    },
    numSubjects: {
      type: Number,
      required: true,
    },
    longitudinal: {
      type: Boolean,
      default: false,
    },
    chartWidth: {
      type: Number,
      default: 240,
    },
    chartHeight: {
      type: Number,
      default: 82,
    },
  },
  data() {
    return {
      missingDomainFallback: missingDomainFallback,
      X_AXIS_HEIGHT,
      // tallest bar and the level labels under it, reported by the sparkline
      // once it has binned the values
      axes: { peak: 0, levels: [] },
    };
  },
  computed: {
    ...mapState('datasetSummary', {
      variableData: state.VARIABLE_DATA,
      visitVariable: state.VISIT_VARIABLE,
      useLongScaleNames: state.USE_LONG_SCALE_NAMES,
    }),
    name() {
      return this.useLongScaleNames
        ? this.variable.label
        : this.variable.abbreviation;
    },
    continuous() {
      return this.variable.data_category === DATA_CATEGORY_CONTINUOUS;
    },
    chartId() {
      return 'dd-' + this.variable.uid;
    },
    axisHeight() {
      return this.chartHeight - CHART_VERTICAL_MARGIN;
    },
    chartRowHeight() {
      return this.axisHeight + X_AXIS_HEIGHT;
    },
    peak() {
      return this.axes.peak;
    },
    // Bar order comes from the sparkline rather than from levelCounts here, so
    // a label always sits under the bar it belongs to.
    xLabels() {
      const levels = this.axes.levels;
      return levels.length > MAX_LABELLED_LEVELS ? [] : levels;
    },
    // The labels are positioned absolutely and so cannot size their own box.
    // `ch` resolves against the digit width of .dd-yaxis's own font, and every
    // label here is digits, so this is exact rather than estimated.
    axisWidth() {
      return `${String(this.tick).length + 0.6}ch`;
    },
    tick() {
      return axisTick(this.peak);
    },
    // The peak bar tops out at the axis, so the tick sits at its share of the
    // height. Clamped to keep the label inside the plot, which only bites when
    // the tick is within a few pixels of the peak anyway.
    tickBottom() {
      if (!this.peak) return 0;
      const centre = (this.tick / this.peak) * this.axisHeight;
      const bottom = centre - TICK_LABEL_HEIGHT / 2;
      return Math.min(Math.max(bottom, 0), this.axisHeight - TICK_LABEL_HEIGHT);
    },
    // Vue caches this per card, so re-rendering the grid does not recompute
    // the reduction for variables whose inputs have not changed
    values() {
      const bySubject = baselineValuesBySubject(
        this.variableData,
        this.variable,
        {
          longitudinal: this.longitudinal,
          visitVariable: this.visitVariable,
        }
      );
      return Array.from(bySubject.values());
    },
    stats() {
      return summaryStats(this.values, this.variable, this.numSubjects);
    },
    footer() {
      const miss = `miss ${formatPercent(this.stats.missingPct)}`;
      if (this.stats.n === 0) {
        return `n 0 · ${miss}`;
      }
      if (this.continuous) {
        return [
          `μ ${formatStat(this.stats.mean)}`,
          `σ ${formatStat(this.stats.sd)}`,
          miss,
        ].join(' · ');
      }
      const levels = this.stats.levels;
      return `${levels} level${levels === 1 ? '' : 's'} · ${miss}`;
    },
  },
};
</script>

<style scoped>
.dd-card {
  cursor: pointer;
  height: 100%;
}
/* the svg renders at chartHeight less its own vertical margin, so the axis
   gutter and the plot both start at the top of this row and line up without an
   offset; the label band then sits under the plot only */
.dd-chart {
  align-items: flex-start;
}
/* the sparkline stretches to the card, which the grid sizes by breakpoint */
.dd-plot {
  flex: 1 1 auto;
  min-width: 0;
}
.dd-plot > div {
  width: 100%;
}
/* an inline svg would sit on a text baseline and leave a descender gap below
   the plot, which would offset it from the labels beside it */
.dd-plot ::v-deep svg {
  display: block;
}
/* the labels are positioned by value against this box, which is the svg's
   drawing area, so the border doubles as the axis line */
.dd-yaxis {
  position: relative;
  flex: 0 0 auto;
  /* the label font lives here so axisWidth's `ch` unit measures these digits */
  font-size: 9px;
  line-height: 1;
  color: #999;
  border-right: 1px solid rgba(0, 0, 0, 0.08);
}
.dd-tick {
  position: absolute;
  right: 2px;
  white-space: nowrap;
}
/* scaleBand pads every band equally, so equal flex children centre each label
   under its own bar at any card width, with no pixel arithmetic */
.dd-xaxis {
  display: flex;
  font-size: 9px;
  line-height: 1.2;
  color: #999;
}
.dd-xlabel {
  flex: 1 1 0;
  min-width: 0;
  padding: 0 1px;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.dd-type {
  font-size: 10px;
  color: #777;
  white-space: nowrap;
}
.dd-footer {
  font-size: 10px;
  color: #777;
}
/* the row aligns its contents to the top for the plot's sake, so the empty
   state centres itself instead */
.dd-empty {
  margin: auto;
  font-size: 11px;
  color: #999;
  font-style: italic;
}
</style>
